import React, { useRef, useState, useEffect } from 'react';
import Hls from 'hls.js';
import { DEFAULT_VOLUME } from '../utils/constants';

export const HlsVideoPlayer = React.memo(({ src, unitId, muted = true, compact = false, onAlive }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoError, setVideoError] = useState(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (!src) {
      if (video.src) {
        video.removeAttribute('src');
        video.load();
      }
      setIsPlaying(false);
      setVideoError(null);
      return;
    }

    const useNative = video.canPlayType('application/vnd.apple.mpegurl');

    const handlePlaying = () => {
      setIsPlaying(true);
      setVideoError(null);
      onAlive?.(unitId);
    };

    const handleError = (e) => {
      console.error('HLS video error', e);
      setVideoError('Video error');
      setIsPlaying(false);
    };

    video.addEventListener('playing', handlePlaying);
    video.addEventListener('error', handleError);

    if (useNative) {
      video.src = src;
      video.muted = muted;
      video.play().catch((err) => {
        console.warn('Native HLS play error', err);
        setVideoError('Cannot play');
      });
    } else if (Hls.isSupported()) {
      const hls = new Hls({
        lowLatencyMode: true,
        backBufferLength: 15,
        maxBufferLength: 5,
        maxMaxBufferLength: 10,
        enableWorker: true,
      });
      hlsRef.current = hls;
      hls.attachMedia(video);
      hls.loadSource(src);

      hls.on(Hls.Events.ERROR, (_, data) => {
        console.warn('HLS.js error', data);
        if (data.fatal) {
          setVideoError(data.details || 'HLS fatal error');
          setIsPlaying(false);
        }
      });

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.muted = muted;
        video.play().catch((err) => {
          console.warn('HLS.js play error', err);
          setVideoError('Cannot play');
        });
      });

      hls.on(Hls.Events.LEVEL_UPDATED, () => setVideoError(null));
    } else {
      setVideoError('HLS not supported');
    }

    return () => {
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('error', handleError);
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [src, muted, unitId, onAlive]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = muted;
    }
  }, [muted]);

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: '100%',
      backgroundColor: '#1a1a1a',
      overflow: 'hidden',
    }}>
      <video
        ref={videoRef}
        playsInline
        autoPlay
        muted={muted}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          backgroundColor: '#1a1a1a',
        }}
      />

      {videoError && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: 'white',
          backgroundColor: 'rgba(0,0,0,0.85)',
          padding: compact ? '8px 12px' : '12px 20px',
          borderRadius: '8px',
          fontSize: compact ? '11px' : '14px',
          textAlign: 'center',
        }}>
          {videoError}
        </div>
      )}

      {compact && isPlaying && (
        <div style={{
          position: 'absolute',
          top: '8px',
          left: '8px',
          backgroundColor: '#ef4444',
          color: 'white',
          padding: '4px 8px',
          borderRadius: '6px',
          fontSize: '10px',
          fontWeight: '700',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        }}>
          <span style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: 'white',
            animation: 'pulse 2s infinite',
          }} />
          LIVE
        </div>
      )}
    </div>
  );
});

export const VideoPlayer = React.memo(({ stream, unitId, muted = true, compact = false, onKeyFrame, onAlive }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoError, setVideoError] = useState(null);
  const currentTrackIdRef = useRef(null);
  const setupInProgressRef = useRef(false);
  const retryCountRef = useRef(0);
  const recoveryTimeoutRef = useRef(null);
  const blackScreenTimeoutRef = useRef(null);
  const muteGraceTimeoutRef = useRef(null);
  const muteRecoveryAttemptRef = useRef(0);
  const lastPlayingTimeRef = useRef(Date.now());

  const videoTrackId = stream?.getVideoTracks()[0]?.id || null;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (setupInProgressRef.current) return;

    // Clear any pending recovery
    if (recoveryTimeoutRef.current) {
      clearTimeout(recoveryTimeoutRef.current);
      recoveryTimeoutRef.current = null;
    }
    if (blackScreenTimeoutRef.current) {
      clearTimeout(blackScreenTimeoutRef.current);
      blackScreenTimeoutRef.current = null;
    }
    if (muteGraceTimeoutRef.current) {
      clearTimeout(muteGraceTimeoutRef.current);
      muteGraceTimeoutRef.current = null;
    }

    if (!stream) {
      if (video.srcObject) video.srcObject = null;
      setIsPlaying(false);
      setVideoError(null);
      currentTrackIdRef.current = null;
      retryCountRef.current = 0;
      return;
    }

    const videoTracks = stream.getVideoTracks();
    if (videoTracks.length === 0) return;

    const track = videoTracks[0];
    const trackId = track.id;

    const existingTrackId = video.srcObject?.getVideoTracks()[0]?.id;
    const needsAttach = !video.srcObject || video.srcObject !== stream;
    if (!needsAttach && (currentTrackIdRef.current === trackId || existingTrackId === trackId)) {
      currentTrackIdRef.current = trackId;
      return;
    }

    setupInProgressRef.current = true;
    currentTrackIdRef.current = trackId;
    retryCountRef.current = 0;

    const tryPlay = () => {
      video.play()
        .then(() => {
          setIsPlaying(true);
          setVideoError(null);
          retryCountRef.current = 0;
          onAlive?.(unitId);
          startBlackScreenDetection();
        })
        .catch(err => {
          console.warn(`Play error for ${unitId}:`, err);
          if (err.name !== 'NotAllowedError') {
            setVideoError('Cannot play');
            scheduleRecovery();
          }
        });
    };

    // Detect black screen by checking if video is actually rendering
    const startBlackScreenDetection = () => {
      if (blackScreenTimeoutRef.current) {
        clearTimeout(blackScreenTimeoutRef.current);
      }
      blackScreenTimeoutRef.current = setTimeout(() => {
        if (video && video.readyState >= 2) {
          // Check if video is actually playing (currentTime changing)
          const currentTime = video.currentTime;
          setTimeout(() => {
            if (video && video.currentTime === currentTime && isPlaying) {
              console.warn(`Black screen detected for ${unitId} - video frozen`);
              onKeyFrame?.(unitId);
              scheduleRecovery();
            }
          }, 2000);
        }
      }, 5000);
    };

    const scheduleRecovery = () => {
      if (retryCountRef.current >= 5) {
        console.error(`Max retries reached for ${unitId}`);
        setVideoError('Connection lost');
        return;
      }

      retryCountRef.current++;
      const delay = Math.min(1000 * retryCountRef.current, 5000);

      console.log(`Scheduling recovery for ${unitId} in ${delay}ms (attempt ${retryCountRef.current})`);

      recoveryTimeoutRef.current = setTimeout(() => {
        if (video && stream) {
          // Re-attach stream
          video.srcObject = null;
          setTimeout(() => {
            if (video && stream) {
              video.srcObject = stream;
              tryPlay();
            }
          }, 100);
        }
      }, delay);
    };

    const handleError = (e) => {
      console.error(`Video error for ${unitId}:`, e);
      setVideoError('Video error');
      setIsPlaying(false);
      scheduleRecovery();
    };

    const handleStalled = () => {
      console.warn(`Video stalled for ${unitId}`);
      setVideoError('Buffering...');
      onKeyFrame?.(unitId);
      onAlive?.(unitId);

      // Auto-recover after 3 seconds of stall
      recoveryTimeoutRef.current = setTimeout(() => {
        if (video && !isPlaying) {
          scheduleRecovery();
        }
      }, 3000);
    };

    const handleWaiting = () => {
      onKeyFrame?.(unitId);
      onAlive?.(unitId);
    };

    const handlePlaying = () => {
      setIsPlaying(true);
      setVideoError(null);
      retryCountRef.current = 0;
      muteRecoveryAttemptRef.current = 0;
      lastPlayingTimeRef.current = Date.now();
      onAlive?.(unitId);
      startBlackScreenDetection();
    };

    const handleCanPlay = () => {
      setVideoError(null);
      tryPlay();
    };

    const handlePause = () => {
      // Auto-resume if paused unexpectedly (not by user)
      if (video && stream && stream.active) {
        setTimeout(() => {
          if (video && video.paused && stream.active) {
            console.log(`Auto-resuming paused video for ${unitId}`);
            tryPlay();
          }
        }, 500);
      }
    };

    const handleEnded = () => {
      console.warn(`Video ended for ${unitId}`);
      setIsPlaying(false);
      // Try to restart
      if (stream && stream.active) {
        scheduleRecovery();
      }
    };

    // Track ended handler
    const handleTrackEnded = () => {
      console.warn(`Video track ended for ${unitId}`);
      setIsPlaying(false);
      setVideoError('Stream ended');
    };

    // Track mute handler (track disabled) - with grace period
    const handleTrackMute = () => {
      console.warn(`Video track muted for ${unitId} - starting grace period`);

      // Clear any existing grace timeout
      if (muteGraceTimeoutRef.current) {
        clearTimeout(muteGraceTimeoutRef.current);
      }

      // Request keyframe immediately - might help recovery
      onKeyFrame?.(unitId);
      onAlive?.(unitId);

      // Don't show error immediately - wait 2 seconds (grace period)
      // Many temporary network hiccups resolve within this time
      muteGraceTimeoutRef.current = setTimeout(() => {
        const track = stream?.getVideoTracks()[0];

        // Check if track is still muted after grace period
        if (track && track.muted) {
          muteRecoveryAttemptRef.current++;

          // Only show "Stream paused" if video was playing recently (within 10s)
          // This prevents false alarms during initial connection
          const wasRecentlyPlaying = Date.now() - lastPlayingTimeRef.current < 10000;

          if (muteRecoveryAttemptRef.current >= 3) {
            // After 3 attempts (~6 seconds), show the error
            setVideoError('Stream paused');
            console.warn(`Video track still muted for ${unitId} after ${muteRecoveryAttemptRef.current} attempts`);
          } else if (wasRecentlyPlaying) {
            // Try keyframe request again and schedule another check
            console.log(`Recovery attempt ${muteRecoveryAttemptRef.current} for ${unitId}`);
            onKeyFrame?.(unitId);

            // Schedule another check
            muteGraceTimeoutRef.current = setTimeout(() => {
              if (track && track.muted) {
                handleTrackMute(); // Recursive check
              }
            }, 2000);
          }
        }
      }, 2000);
    };

    const handleTrackUnmute = () => {
      console.log(`Video track unmuted for ${unitId}`);

      // Clear grace timeout
      if (muteGraceTimeoutRef.current) {
        clearTimeout(muteGraceTimeoutRef.current);
        muteGraceTimeoutRef.current = null;
      }

      // Reset recovery attempts
      muteRecoveryAttemptRef.current = 0;

      setVideoError(null);
      tryPlay();
    };

    // Add event listeners
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);
    video.addEventListener('stalled', handleStalled);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);

    // Track event listeners
    track.addEventListener('ended', handleTrackEnded);
    track.addEventListener('mute', handleTrackMute);
    track.addEventListener('unmute', handleTrackUnmute);

    // Now set the stream
    if (needsAttach) {
      video.srcObject = stream;
    }
    video.volume = muted ? 0 : DEFAULT_VOLUME;
    setupInProgressRef.current = false;

    // Try to play immediately
    const playTimeout = setTimeout(() => {
      if (video.readyState >= 3) {
        tryPlay();
      }
    }, 100);

    return () => {
      clearTimeout(playTimeout);
      if (recoveryTimeoutRef.current) {
        clearTimeout(recoveryTimeoutRef.current);
      }
      if (blackScreenTimeoutRef.current) {
        clearTimeout(blackScreenTimeoutRef.current);
      }
      if (muteGraceTimeoutRef.current) {
        clearTimeout(muteGraceTimeoutRef.current);
      }
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);
      video.removeEventListener('stalled', handleStalled);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      track.removeEventListener('ended', handleTrackEnded);
      track.removeEventListener('mute', handleTrackMute);
      track.removeEventListener('unmute', handleTrackUnmute);
    };
  }, [videoTrackId, stream, unitId, muted, onKeyFrame, onAlive]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = muted ? 0 : DEFAULT_VOLUME;
    }
  }, [muted]);

  useEffect(() => {
    return () => {
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, []);

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: '100%',
      backgroundColor: '#1a1a1a',
      overflow: 'hidden',
    }}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={muted}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          backgroundColor: '#1a1a1a',
        }}
      />

      {videoError && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: 'white',
          backgroundColor: 'rgba(0,0,0,0.85)',
          padding: compact ? '8px 12px' : '12px 20px',
          borderRadius: '8px',
          fontSize: compact ? '11px' : '14px',
          textAlign: 'center',
        }}>
          {videoError}
        </div>
      )}

      {compact && isPlaying && (
        <div style={{
          position: 'absolute',
          top: '8px',
          left: '8px',
          backgroundColor: '#ef4444',
          color: 'white',
          padding: '4px 8px',
          borderRadius: '6px',
          fontSize: '10px',
          fontWeight: '700',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        }}>
          <span style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: 'white',
            animation: 'pulse 2s infinite',
          }} />
          LIVE
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  const prevTrackId = prevProps.stream?.getVideoTracks()[0]?.id;
  const nextTrackId = nextProps.stream?.getVideoTracks()[0]?.id;

  return (
    prevTrackId === nextTrackId &&
    prevProps.muted === nextProps.muted &&
    prevProps.unitId === nextProps.unitId
  );
});
